import { ADMIN_ROLES, Role, type AdvisorFunction } from './enums';
import { ROLE_LABELS } from './constants';

/**
 * Jerarquía de roles para administrar usuarios: nadie toca a alguien con un rol superior al suyo
 * en esa empresa. Solo ADMIN y GERENTE administran, así que en la práctica la regla separa a los
 * administradores (que solo otro ADMIN modifica) del resto.
 */
export const ROLE_RANK: Record<Role, number> = {
  ADMIN: 7,
  GERENTE: 6,
  JEFE_COMERCIAL: 5,
  COORDINADOR: 4,
  MARKETING: 3,
  ASESOR: 2,
  LECTURA: 1,
  BOT: 0,
};

/** Una línea por rol para la pantalla de usuarios. */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  ADMIN: 'Todo: configura la empresa, crea usuarios y nombra administradores.',
  GERENTE: 'Administra la empresa y los usuarios, pero no puede nombrar administradores.',
  JEFE_COMERCIAL: 'Ve y gestiona todos los leads de su alcance y reparte entre asesores.',
  COORDINADOR: 'Ve y gestiona todos los leads de su alcance y reparte entre asesores.',
  ASESOR: 'Gestiona solo sus propios leads.',
  MARKETING: 'Ve todos los leads y reportes; no los gestiona.',
  LECTURA: 'Solo ve; no cambia nada.',
  BOT: 'Cuenta de servicio (API key): gestiona leads de su alcance, no reasigna.',
};

export const ADVISOR_FUNCTION_LABELS: Record<AdvisorFunction, string> = {
  DIGITAL: 'Digital',
  VENTAS: 'Ventas',
  HIBRIDO: 'Híbrido',
};

/** Etiqueta de la función comercial (solo informativa; vacía si no tiene). */
export function advisorFunctionLabel(value: string | null | undefined): string {
  return value && value in ADVISOR_FUNCTION_LABELS
    ? ADVISOR_FUNCTION_LABELS[value as AdvisorFunction]
    : '';
}

/** Rol de mayor jerarquía de una lista (null si está vacía). */
export function highestRole(roles: readonly Role[]): Role | null {
  let best: Role | null = null;
  for (const r of roles) if (best === null || ROLE_RANK[r] > ROLE_RANK[best]) best = r;
  return best;
}

/**
 * Roles que alguien con `actorRoles` en una empresa puede otorgar ahí: un ADMIN cualquiera salvo
 * BOT (se crea desde API keys); un GERENTE además no otorga ADMIN; los demás, ninguno.
 */
export function assignableRoles(actorRoles: readonly Role[]): Role[] {
  const top = highestRole(actorRoles);
  if (!top || !ADMIN_ROLES.includes(top)) return [];
  return Object.values(Role).filter((r) => r !== 'BOT' && ROLE_RANK[r] <= ROLE_RANK[top]);
}

export interface ManagedMembership {
  companyId: string;
  companyName?: string;
  role: Role;
}

export interface UserManageCheck {
  ok: boolean;
  /** Motivo en español cuando `ok` es false (el mismo que devuelve la API). */
  reason: string | null;
}

/**
 * ¿Puede el actor (roles por empresa) editar, cambiar la clave, desactivar o cambiar las membresías
 * del usuario destino? Reglas: el destino debe pertenecer a alguna empresa que el actor administra,
 * todas sus empresas deben ser administradas por el actor y en ninguna puede tener un rol superior
 * al del actor (un ADMIN solo lo toca otro ADMIN).
 */
export function checkUserManageable(
  actorRolesByCompany: ReadonlyMap<string, readonly Role[]>,
  target: readonly ManagedMembership[],
): UserManageCheck {
  const administers = (companyId: string) =>
    (actorRolesByCompany.get(companyId) ?? []).some((r) => ADMIN_ROLES.includes(r));
  if (!target.some((m) => administers(m.companyId)))
    return { ok: false, reason: 'El usuario no pertenece a tus empresas' };
  const foreign = target.filter((m) => !administers(m.companyId));
  if (foreign.length > 0) {
    const names = [...new Set(foreign.map((m) => m.companyName).filter(Boolean))];
    return {
      ok: false,
      reason: `También pertenece a empresas que no administras${names.length ? ` (${names.join(', ')})` : ''}`,
    };
  }
  for (const m of target) {
    const mine = highestRole(actorRolesByCompany.get(m.companyId) ?? []);
    if (!mine || ROLE_RANK[m.role] > ROLE_RANK[mine]) {
      const label = ROLE_LABELS[m.role] ?? m.role;
      return {
        ok: false,
        reason:
          m.role === 'ADMIN'
            ? `Solo un administrador puede modificar a otro administrador${m.companyName ? ` (${m.companyName})` : ''}`
            : `Tiene un rol superior al tuyo (${label})${m.companyName ? ` en ${m.companyName}` : ''}`,
      };
    }
  }
  return { ok: true, reason: null };
}
